import React, { useState } from 'react';
import { api } from '../api';
import Icon from './ui/Icon';
import Btn from './ui/Btn';
import Header from './ui/Header';

interface SettingsSectionProps {
  theme: string;
  setTheme: (t: string) => void;
  onLogout: () => void;
}

const SettingsSection: React.FC<SettingsSectionProps> = ({ theme, setTheme, onLogout }) => {
  const [cleaned, setCleaned] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const Toggle: React.FC<{ value: boolean; onChange: (v: boolean) => void }> = ({ value, onChange }) => (
    <button
      type="button"
      onClick={() => onChange(!value)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        border: '1px solid var(--glass-border)',
        cursor: 'pointer',
        background: value ? 'var(--teal-solid)' : 'var(--glass-bg)',
        position: 'relative',
        transition: 'background 0.2s',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <span style={{
        position: 'absolute',
        top: 3,
        left: value ? 22 : 3,
        width: 16,
        height: 16,
        borderRadius: 8,
        background: value ? '#fff' : 'var(--text2)',
        transition: 'left 0.2s',
        display: 'block',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </button>
  );

  const SRow: React.FC<{
    icon: string;
    label: string;
    sub?: string;
    right?: React.ReactNode;
    danger?: boolean;
  }> = ({ icon, label, sub, right, danger }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      padding: '14px 20px',
      borderBottom: '1px solid var(--glass-border)',
    }}>
      <span style={{ color: danger ? 'var(--red)' : 'var(--text2)', flexShrink: 0 }}>
        <Icon name={icon} size={19} color={danger ? 'var(--red)' : 'var(--text1)'} />
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: danger ? 'var(--red)' : 'var(--text0)' }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );

  const SGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div>
      <div style={{
        padding: '16px 20px 8px',
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--text2)',
        textTransform: 'uppercase',
        letterSpacing: '0.09em',
      }}>
        {label}
      </div>
      <div className="glass" style={{ borderRadius: 'var(--r-md)', margin: '0 12px', overflow: 'hidden' }}>
        <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
      </div>
    </div>
  );

  const handleCleanup = async () => {
    setCleaning(true);
    try {
      await api.cleanupImages();
      setCleaned(true);
    } catch (err) {
      console.error('Cleanup error', err);
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header title="Настройки" />
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 24 }}>
        <SGroup label="Внешний вид">
          <SRow
            icon={theme === 'dark' ? 'moon' : 'sun'}
            label="Тема"
            sub={theme === 'dark' ? 'Тёмная' : 'Светлая'}
            right={
              <div style={{ display: 'flex', gap: 6 }}>
                {['dark', 'light'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTheme(t)}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 'var(--r-sm)',
                      border: '1px solid',
                      cursor: 'pointer',
                      fontFamily: 'var(--sans)',
                      fontSize: 12,
                      fontWeight: 500,
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      background: theme === t ? 'var(--teal-solid)' : 'var(--glass-bg)',
                      color: theme === t ? '#0d1117' : 'var(--text2)',
                      borderColor: theme === t ? 'var(--teal)' : 'var(--glass-border)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {t === 'dark' ? '🌑 Тёмная' : '☀️ Светлая'}
                  </button>
                ))}
              </div>
            }
          />
        </SGroup>

        <SGroup label="Безопасность">
          <SRow
            icon="logout"
            label="Выйти из аккаунта"
            sub="Завершить текущую сессию"
            danger
            right={
              <Btn variant="danger" onClick={onLogout} style={{ padding: '5px 12px', fontSize: 13 }}>
                Выйти
              </Btn>
            }
          />
        </SGroup>

        <SGroup label="Файлы">
          <SRow
            icon="file_x"
            label="Очистка файлов"
            sub="Удалить неиспользуемые фото"
            right={
              <Btn
                variant="ghost"
                onClick={handleCleanup}
                disabled={cleaning}
                style={{ padding: '5px 12px', fontSize: 13 }}
              >
                {cleaned ? '✓ Готово' : cleaning ? 'Очистка...' : 'Очистить'}
              </Btn>
            }
          />
        </SGroup>

        <SGroup label="О приложении">
          <SRow icon="chips" label="ChipBASE" sub="Управление мастерской" />
          <SRow
            icon="info"
            label="Версия"
            right={<span style={{ fontFamily: 'var(--mono)', color: 'var(--text2)', fontSize: 13 }}>v2.0.0</span>}
          />
        </SGroup>
      </div>
    </div>
  );
};

export default SettingsSection;
